import { ReactNode } from "react";
import { Focusable, TooltipTrigger } from "react-aria-components";
import { TooltipBubble } from "../TooltipBubble";

interface ElementIconProps {
  /**
   * The accessible label for the icon (used for aria-label).
   */
  label: string;
  /**
   * The content to render inside the wrapper (typically an SVG).
   *
   * **Accessibility:** The wrapper span provides the accessible name via `aria-label`.
   * To avoid duplicate announcements, pass the SVG with `aria-hidden`.
   */
  children: ReactNode;
  /**
   * Optional text for the tooltip bubble. Only provide this if it adds information beyond `label`.
   * If omitted, the tooltip will show `label` visually but is hidden from screen readers to
   * prevent redundant announcements. Ensure `label` contains all essential information.
   */
  tooltipText?: string;
}

/**
 * Renders a focusable status icon with a tooltip.
 *
 * **When to use:**
 * - Non-interactive status indicators (e.g., Captain, Vice Captain)
 * - Informational badges that need tooltip explanations
 *
 * **When NOT to use:**
 * - For interactive actions (use `ElementIconButton` instead)
 * - When you need click handlers (use `ElementIconButton` instead)
 *
 * **Accessibility:**
 * - The wrapper provides the accessible name via `aria-label={label}` - this is always announced
 * - Provide `tooltipText` only when it adds information beyond `label` (e.g., extra context or explanation)
 * - If `tooltipText` is omitted, the tooltip shows `label` visually but is hidden from screen readers
 * - Ensure `label` contains all essential information for screen reader users
 * - Pass SVG icons with `aria-hidden` to avoid duplicate announcements
 *
 * @example
 * ```tsx
 * // Good - status icon with tooltip
 * <ElementIcon label="Team of the Week">
 *   <DreamTeam aria-hidden />
 * </ElementIcon>
 *
 * // Good - separate label and tooltip
 * <ElementIcon label="Team of the Week" tooltipText="In the Team of the Week">
 *   <DreamTeam aria-hidden />
 * </ElementIcon>
 * ```
 */
const ElementIcon = ({ label, children, tooltipText }: ElementIconProps) => (
  <TooltipTrigger delay={100}>
    <Focusable>
      <span role="img" aria-label={label}>
        {children}
      </span>
    </Focusable>
    <TooltipBubble>
      {tooltipText && tooltipText !== label ? (
        tooltipText
      ) : (
        <span aria-hidden="true">{label}</span>
      )}
    </TooltipBubble>
  </TooltipTrigger>
);

export default ElementIcon;
